import type { UIMessage } from "ai";
import { useMemo } from "react";
import { approvalChoiceUserText } from "./approvalChoiceUserText";
import { WRITE_APPROVAL_CHOICE_MESSAGE_METADATA } from "./hiddenChatUiMessages";

type ActivePending = {
  toolCallId: string;
  runId: string;
  mode: "create" | "update";
  approvalReady: boolean;
} | null;

type Args = {
  activePending: ActivePending;
  busy: boolean;
  appendUserChatMessage: (userText: string, metadata?: unknown) => UIMessage[];
  postApprovalAction: (
    path: "approve" | "decline",
    body: Record<string, unknown>,
    messagesForRequest?: UIMessage[],
    options?: { declineReason?: string }
  ) => Promise<void>;
  clearApproval: (toolCallId: string) => void;
};

export type ApprovalBarProps = {
  disabled?: boolean;
  onCreate: () => void;
  onViewPlan: () => void;
};

function buildActions(
  activePending: NonNullable<ActivePending>,
  busy: boolean,
  appendUserChatMessage: (userText: string, metadata?: unknown) => UIMessage[],
  postApprovalAction: Args["postApprovalAction"],
  clearApproval: (toolCallId: string) => void,
  onViewPlan: () => void
): ApprovalBarProps {
  const disabled = busy || !activePending.approvalReady;
  const mode = activePending.mode;

  return {
    disabled,
    onViewPlan: () => {
      if (disabled) return;
      onViewPlan();
    },
    onCreate: () => {
      if (!activePending.approvalReady) return;
      const userText = approvalChoiceUserText({ choice: "create", mode });
      const withUser = appendUserChatMessage(userText, WRITE_APPROVAL_CHOICE_MESSAGE_METADATA);
      void postApprovalAction(
        "approve",
        {
          runId: activePending.runId,
          toolCallId: activePending.toolCallId,
        },
        withUser
      ).then(() => clearApproval(activePending.toolCallId));
    },
  };
}

export function useAssistantWriteApprovalHandlers({
  activePending,
  busy,
  appendUserChatMessage,
  postApprovalAction,
  clearApproval,
  onViewPlan,
}: Args & { onViewPlan: () => void }) {
  const pendingDockActions = useMemo((): ApprovalBarProps | null => {
    if (!activePending) return null;
    return buildActions(
      activePending,
      busy,
      appendUserChatMessage,
      postApprovalAction,
      clearApproval,
      onViewPlan
    );
  }, [
    activePending,
    busy,
    appendUserChatMessage,
    postApprovalAction,
    clearApproval,
    onViewPlan,
  ]);

  const revisePendingProposal = useMemo(() => {
    if (!activePending?.approvalReady) return null;
    return (userText: string, messagesWithUser: UIMessage[]) => {
      void postApprovalAction(
        "decline",
        {
          runId: activePending.runId,
          toolCallId: activePending.toolCallId,
          reason: userText,
        },
        messagesWithUser,
        { declineReason: userText }
      ).then(() => clearApproval(activePending.toolCallId));
    };
  }, [activePending, postApprovalAction, clearApproval]);

  return useMemo(
    () => ({ pendingDockActions, revisePendingProposal }),
    [pendingDockActions, revisePendingProposal]
  );
}
